import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Navigate, useNavigate } from 'react-router-dom'
import { AdminLoginRequestSchema, type AdminLoginRequest } from '@naya/shared'
import { AdminApiError } from '../adminApi.js'
import { useAdminLogin, useAdminMe } from '../hooks/useAdminMe.js'
import { PasswordInput } from '../../shared/PasswordInput.js'

export function Login() {
  const navigate = useNavigate()
  const { data: me, isLoading: meLoading } = useAdminMe()
  const login = useAdminLogin()

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<AdminLoginRequest>({
    resolver: zodResolver(AdminLoginRequestSchema),
  })

  // Already signed in → skip the form.
  useEffect(() => {
    if (me) navigate('/admin/tenants', { replace: true })
  }, [me, navigate])

  if (!meLoading && me) return <Navigate to="/admin/tenants" replace />

  function onSubmit(values: AdminLoginRequest) {
    login.mutate(values, {
      onSuccess: () => navigate('/admin/tenants', { replace: true }),
      onError: (err) => {
        // Surface throttle / bad-credential errors at the form level.
        const message =
          err instanceof AdminApiError
            ? err.status === 429
              ? err.message || 'Too many attempts — try again later.'
              : err.userMessage
            : 'Something went wrong — try again.'
        setError('root', { message })
      },
    })
  }

  return (
    <div className="admin-login-wrap">
      <div className="admin-card admin-login-card">
        <div className="admin-section-label">
          <span className="idx">00 —</span>
          <span>Superadmin</span>
        </div>
        <div className="admin-login-mark">
          Naya<span>.</span>
        </div>
        <p className="admin-login-sub">Sign in to manage tenants.</p>

        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          {errors.root && (
            <div className="admin-inline-error" role="alert">
              {errors.root.message}
            </div>
          )}

          <div className="admin-field">
            <label className="admin-label" htmlFor="email">
              Email
            </label>
            <input
              id="email"
              type="email"
              inputMode="email"
              autoComplete="username"
              className="admin-input"
              {...register('email')}
            />
            {errors.email && (
              <p className="admin-field-error">{errors.email.message}</p>
            )}
          </div>

          <div className="admin-field">
            <label className="admin-label" htmlFor="password">
              Password
            </label>
            <PasswordInput
              id="password"
              classPrefix="admin"
              autoComplete="current-password"
              {...register('password')}
            />
            {errors.password && (
              <p className="admin-field-error">{errors.password.message}</p>
            )}
          </div>

          <button
            type="submit"
            className="admin-btn admin-btn-primary"
            style={{ width: '100%' }}
            disabled={isSubmitting || login.isPending}
          >
            {login.isPending ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  )
}
